import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Pencil, Trash2, Video, FileText, Upload, Link as LinkIcon,
  FolderOpen, Search, X, ExternalLink, Download, Play, Settings
} from 'lucide-react';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

import { API, API_BASE } from '../config/api';
const BACKEND_URL = API_BASE;

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

function AdminResources({ user }) {
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('resources');
  
  // Resource Modal State
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    resource_type: 'document',
    video_type: 'youtube',
    url: '',
    category_id: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Category Modal State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: ''
  });
  
  // Delete Confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: '', item: null });

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resourcesRes, categoriesRes] = await Promise.all([
        fetch(`${API}/resources`, { credentials: 'include' }),
        fetch(`${API}/resource-categories`, { credentials: 'include' })
      ]);
      
      if (resourcesRes.ok) setResources(await resourcesRes.json());
      if (categoriesRes.ok) setCategories(await categoriesRes.json());
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Category Functions
  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      const url = editingCategory 
        ? `${API}/resource-categories/${editingCategory.category_id}`
        : `${API}/resource-categories`;
      
      const response = await fetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(categoryForm)
      });

      if (response.ok) {
        toast.success(editingCategory ? 'Category updated' : 'Category created');
        setShowCategoryModal(false);
        setEditingCategory(null);
        setCategoryForm({ name: '', description: '', icon: '' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save category');
      }
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteConfirm.item) return;
    
    try {
      const response = await fetch(`${API}/resource-categories/${deleteConfirm.item.category_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Category deleted');
        setDeleteConfirm({ show: false, type: '', item: null });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete category');
      }
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Resource Functions
  const handleSaveResource = async () => {
    if (!resourceForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!resourceForm.category_id) {
      toast.error('Please select a category');
      return;
    }

    // For new resources, either URL or file is required
    if (!editingResource && !resourceForm.url && !selectedFile) {
      toast.error('Please provide a URL or upload a file');
      return;
    }

    setUploading(true);
    
    try {
      let response;
      
      if (selectedFile) {
        // Upload with file
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('title', resourceForm.title);
        formData.append('description', resourceForm.description);
        formData.append('resource_type', resourceForm.resource_type);
        formData.append('category_id', resourceForm.category_id);
        if (resourceForm.resource_type === 'video') {
          formData.append('video_type', 'upload');
        }
        
        response = await fetch(`${API}/resources/upload`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
      } else if (editingResource) {
        // Update existing resource
        response = await fetch(`${API}/resources/${editingResource.resource_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: resourceForm.title,
            description: resourceForm.description,
            url: resourceForm.url,
            category_id: resourceForm.category_id
          })
        });
      } else {
        // Create new resource with URL
        response = await fetch(`${API}/resources`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...resourceForm,
            video_type: resourceForm.resource_type === 'video' ? resourceForm.video_type : null
          })
        });
      }

      if (response.ok) {
        toast.success(editingResource ? 'Resource updated' : 'Resource created');
        setShowResourceModal(false);
        setEditingResource(null);
        setSelectedFile(null);
        setResourceForm({
          title: '',
          description: '',
          resource_type: 'document',
          video_type: 'youtube',
          url: '',
          category_id: ''
        });
        fetchData();
      } else {
        let errorMsg = 'Failed to save resource';
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch (e) {
          errorMsg = `Server error: ${response.status} ${response.statusText}`;
        }
        console.error('Resource save error:', errorMsg, 'Status:', response.status);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Resource save exception:', error);
      toast.error('Failed to save resource: ' + (error.message || 'Network error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteResource = async () => {
    if (!deleteConfirm.item) return;
    
    try {
      const response = await fetch(`${API}/resources/${deleteConfirm.item.resource_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Resource deleted');
        setDeleteConfirm({ show: false, type: '', item: null });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete resource');
      }
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const openEditResource = (resource) => {
    setEditingResource(resource);
    setResourceForm({
      title: resource.title,
      description: resource.description,
      resource_type: resource.resource_type,
      video_type: resource.video_type || 'youtube',
      url: resource.url || '',
      category_id: resource.category_id
    });
    setSelectedFile(null);
    setShowResourceModal(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      icon: category.icon || ''
    });
    setShowCategoryModal(true);
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || resource.category_id === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCFE]">
        <Navigation user={user} onLogout={handleLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#80298F]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <Navigation user={user} onLogout={handleLogout} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Resources</h1>
            <p className="text-gray-600 mt-1">Add, edit, and organize client resources</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setEditingCategory(null);
                setCategoryForm({ name: '', description: '', icon: '' });
                setShowCategoryModal(true);
              }}
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              Add Category
            </Button>
            <Button
              onClick={() => {
                if (categories.length === 0) {
                  toast.error('Please create a category first');
                  return;
                }
                setEditingResource(null);
                setResourceForm({
                  title: '',
                  description: '',
                  resource_type: 'document',
                  video_type: 'youtube',
                  url: '',
                  category_id: categories[0]?.category_id || ''
                });
                setSelectedFile(null);
                setShowResourceModal(true);
              }}
              className="bg-[#80298F] hover:bg-[#9b3ba8]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Resource
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="resources">
              Resources ({resources.length})
            </TabsTrigger>
            <TabsTrigger value="categories">
              Categories ({categories.length})
            </TabsTrigger>
          </TabsList>

          {/* Resources Tab */}
          <TabsContent value="resources">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search resources..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#80298F]"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.category_id} value={cat.category_id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {filteredResources.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No resources found</p>
                  <Button onClick={() => setShowResourceModal(true)} className="bg-[#80298F] hover:bg-[#9b3ba8]">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Resource
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredResources.map(resource => (
                  <Card key={resource.resource_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        {/* Icon */}
                        <div className={`flex-shrink-0 p-3 rounded-lg ${resource.resource_type === 'document' ? 'bg-purple-100' : 'bg-amber-100'}`}>
                          {resource.resource_type === 'document' ? (
                            <FileText className="h-6 w-6 text-[#80298F]" />
                          ) : (
                            <Video className="h-6 w-6 text-[#FAA519]" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900">{resource.title}</h3>
                          <p className="text-sm text-gray-600 line-clamp-1">{resource.description}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span className="px-2 py-0.5 bg-gray-100 rounded">{resource.category_name}</span>
                            <span>{resource.resource_type}</span>
                            {resource.video_type && <span>({resource.video_type})</span>}
                            {resource.file_size && <span>{formatFileSize(resource.file_size)}</span>}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {resource.url && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => window.open(resource.url, '_blank')}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditResource(resource)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteConfirm({ show: true, type: 'resource', item: resource })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            {categories.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No categories yet</p>
                  <Button onClick={() => setShowCategoryModal(true)} className="bg-[#80298F] hover:bg-[#9b3ba8]">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Category
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {categories.map(category => {
                  const resourceCount = resources.filter(r => r.category_id === category.category_id).length;
                  return (
                    <Card key={category.category_id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <FolderOpen className="h-5 w-5 text-[#80298F]" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{category.name}</h3>
                              {category.description && (
                                <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-2">{resourceCount} resources</p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditCategory(category)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteConfirm({ show: true, type: 'category', item: category })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Resource Modal */}
      <Dialog open={showResourceModal} onOpenChange={setShowResourceModal}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingResource ? 'Edit Resource' : 'Add New Resource'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={resourceForm.title}
                onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                placeholder="Resource title"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={resourceForm.description}
                onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                placeholder="Brief description of this resource"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select
                value={resourceForm.category_id}
                onValueChange={(value) => setResourceForm({ ...resourceForm, category_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {!editingResource && (
              <div className="space-y-2">
                <Label>Resource Type *</Label>
                <Select
                  value={resourceForm.resource_type}
                  onValueChange={(value) => setResourceForm({ ...resourceForm, resource_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {!editingResource && resourceForm.resource_type === 'video' && (
              <div className="space-y-2">
                <Label>Video Source</Label>
                <Select
                  value={resourceForm.video_type}
                  onValueChange={(value) => setResourceForm({ ...resourceForm, video_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="youtube">YouTube Link</SelectItem>
                    <SelectItem value="vimeo">Vimeo Link</SelectItem>
                    <SelectItem value="upload">Upload Video File</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* URL Input */}
            {(!editingResource ? (
              (resourceForm.resource_type === 'document' && !selectedFile) ||
              (resourceForm.resource_type === 'video' && resourceForm.video_type !== 'upload')
            ) : true) && (
              <div className="space-y-2">
                <Label>
                  {resourceForm.resource_type === 'video' ? 'Video URL' : 'Document URL'}
                  {!editingResource && ' (or upload file below)'}
                </Label>
                <Input
                  value={resourceForm.url}
                  onChange={(e) => setResourceForm({ ...resourceForm, url: e.target.value })}
                  placeholder={resourceForm.resource_type === 'video' 
                    ? 'https://youtube.com/watch?v=...' 
                    : 'https://example.com/document.pdf'
                  }
                />
              </div>
            )}
            
            {/* File Upload */}
            {!editingResource && (
              (resourceForm.resource_type === 'document' || 
               (resourceForm.resource_type === 'video' && resourceForm.video_type === 'upload'))
            ) && (
              <div className="space-y-2">
                <Label>Upload File</Label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#80298F] transition-colors"
                >
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="h-5 w-5 text-[#80298F]" />
                      <span className="text-sm text-gray-700">{selectedFile.name}</span>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                        className="ml-2 text-gray-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to select a file</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {resourceForm.resource_type === 'video' 
                          ? 'MP4, MOV, AVI, etc.' 
                          : 'PDF, DOC, DOCX, etc.'
                        }
                      </p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="hidden"
                  accept={resourceForm.resource_type === 'video' 
                    ? 'video/*' 
                    : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'
                  }
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResourceModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveResource} 
              disabled={uploading}
              className="bg-[#80298F] hover:bg-[#9b3ba8]"
            >
              {uploading ? 'Saving...' : (editingResource ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add New Category'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Legal Guides"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Brief description of this category"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory} className="bg-[#80298F] hover:bg-[#9b3ba8]">
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, type: '', item: null })}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          
          <p className="py-4">
            Are you sure you want to delete{' '}
            <span className="font-semibold">
              {deleteConfirm.item?.title || deleteConfirm.item?.name}
            </span>
            ? This action cannot be undone.
          </p>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm({ show: false, type: '', item: null })}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={deleteConfirm.type === 'resource' ? handleDeleteResource : handleDeleteCategory}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminResources;
